import { ApiClient, ApiError, type DeploymentRecord } from '../lib/api.js';
import {
  el,
  formatDateTime,
  formatDuration,
  mount,
  statusPill,
  type PillKind,
} from '../lib/dom.js';

export interface LogsViewDeps {
  api: ApiClient;
}

const pillKind = (status: DeploymentRecord['status']): PillKind => status;

export function renderLogs(deps: LogsViewDeps): HTMLElement {
  const list = el('div', { class: 'logs-list' });
  const refreshBtn = el('button', { class: 'ghost', text: 'Refresh' });
  const summary = el('span', { class: 'who' });
  const alertBox = el('div', { class: 'alert', attrs: { hidden: true, role: 'alert' } });

  const showAlert = (msg: string, kind: 'error' | 'success' = 'error') => {
    alertBox.className = kind === 'success' ? 'alert success' : 'alert';
    alertBox.textContent = msg;
    alertBox.removeAttribute('hidden');
    setTimeout(() => alertBox.setAttribute('hidden', ''), 6000);
  };

  const openModal = async (id: number) => {
    const backdrop = el('div', { class: 'modal-backdrop' });
    const closeBtn = el('button', { class: 'ghost', text: 'Close' });
    const content = el('pre', { class: 'log-output', text: 'Loading…' });
    const titleSpan = el('h3', { text: `Deployment #${id}` });
    const headerPill = el('span');
    const modal = el('div', {
      class: 'modal',
      children: [
        el('header', {
          children: [
            el('div', {
              class: 'modal-title',
              children: [titleSpan, headerPill],
            }),
            closeBtn,
          ],
        }),
        content,
      ],
    });
    backdrop.append(modal);
    document.body.append(backdrop);
    const close = () => backdrop.remove();
    closeBtn.addEventListener('click', close);
    backdrop.addEventListener('click', (ev) => {
      if (ev.target === backdrop) close();
    });
    document.addEventListener('keydown', function escHandler(ev) {
      if (ev.key === 'Escape') {
        close();
        document.removeEventListener('keydown', escHandler);
      }
    });
    try {
      const data = await deps.api.getLog(id);
      titleSpan.textContent = `#${data.deployment.id} · ${data.deployment.branch}`;
      headerPill.replaceWith(statusPill(pillKind(data.deployment.status)));
      content.textContent = data.log || '(empty log)';
      content.scrollTop = content.scrollHeight;
    } catch (err) {
      content.textContent = err instanceof ApiError ? err.message : 'Failed to load log';
    }
  };

  const renderRow = (d: DeploymentRecord) => {
    const meta = el('div', {
      class: 'log-meta',
      children: [
        el('div', {
          class: 'log-row-head',
          children: [
            statusPill(pillKind(d.status)),
            el('span', { class: 'branch-tag', text: d.branch }),
          ],
        }),
        el('div', {
          class: 'when',
          text: `${formatDateTime(d.startedAt)} · ${formatDuration(d.startedAt, d.finishedAt)}`,
        }),
        el('div', {
          class: 'who',
          text: `by ${d.username}${d.apiKeyName ? ` (key: ${d.apiKeyName})` : ''}${d.commitSha ? ` · ${d.commitSha.slice(0, 7)}` : ''}${d.errorMessage ? ` · ${d.errorMessage}` : ''}`,
        }),
      ],
    });
    const openBtn = el('button', {
      text: 'View',
      on: {
        click: (ev) => {
          ev.stopPropagation();
          void openModal(d.id);
        },
      },
    });
    const deleteBtn = el('button', {
      class: 'danger',
      text: 'Delete',
      attrs: { title: 'Delete this deployment entry and its log file' },
      on: {
        click: async (ev) => {
          ev.stopPropagation();
          if (!confirm(`Delete deployment #${d.id} (${d.branch})?\nThis also removes its log file.`)) return;
          deleteBtn.setAttribute('disabled', '');
          try {
            await deps.api.deleteLog(d.id);
            showAlert(`Deployment #${d.id} deleted`, 'success');
            await refresh();
          } catch (err) {
            const msg = err instanceof ApiError
              ? err.code === 'deploy_running'
                ? 'Cannot delete a running deployment.'
                : err.message
              : 'Failed to delete';
            showAlert(msg);
            deleteBtn.removeAttribute('disabled');
          }
        },
      },
    });
    if (d.status === 'running') {
      deleteBtn.setAttribute('disabled', '');
      deleteBtn.setAttribute('title', 'Running deployments cannot be deleted');
    }
    const actions = el('div', { class: 'log-actions', children: [openBtn, deleteBtn] });
    return el('div', {
      class: 'log-row',
      on: { click: () => void openModal(d.id) },
      children: [meta, actions],
    });
  };

  const refresh = async () => {
    refreshBtn.setAttribute('disabled', '');
    try {
      const { items } = await deps.api.listLogs(50, 0);
      summary.textContent = items.length ? `${items.length} most recent` : '';
      if (items.length === 0) {
        mount(list, [el('div', { class: 'empty', text: 'No deployments yet.' })]);
        return;
      }
      mount(list, items.map(renderRow));
    } catch (err) {
      showAlert(err instanceof ApiError ? err.message : 'Failed to load deployments');
    } finally {
      refreshBtn.removeAttribute('disabled');
    }
  };

  refreshBtn.addEventListener('click', refresh);
  void refresh();

  return el('div', {
    children: [
      el('div', {
        class: 'card',
        children: [
          el('h2', { text: 'Deployment history' }),
          alertBox,
          el('div', {
            class: 'toolbar',
            children: [el('span', { class: 'spacer' }), summary, refreshBtn],
          }),
          list,
        ],
      }),
    ],
  });
}
